/**
 * Feature Settings Component
 * @description Manage venue feature toggles (Tips, etc.)
 */

import React, { useState } from 'react';
import { Typography, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentVenue, setVenueInfo } from '../../../redux/slices/currentUserSlice';
import { updateVenueTipsFeature } from '../../../redux/venues/venuesService';
import StyledSwitch from '../../StyledSwitch/StyledSwitch';
import {
  FeatureSettingsContainer,
  FeatureSettingsHeader,
  FeaturesList,
  FeatureItem,
  FeatureInfo,
  FeatureTitle,
  FeatureDescription,
  FeatureControl,
} from './FeatureSettings.styles';

const FEATURES = [
  {
    id: 'tips',
    title: 'Tips Tracking',
    description: 'Track and manage employee tips owed from this venue to its employees.',
    venueKey: 'isTipsFeatureEnabled',
    updateFunction: updateVenueTipsFeature,
  },
];

const FeatureSettings = () => {
  const dispatch = useDispatch();
  const currentVenue = useSelector(selectCurrentVenue);
  const [updatingFeatures, setUpdatingFeatures] = useState({});
  const [errors, setErrors] = useState({});

  const handleToggle = async (feature, newValue) => {
    setUpdatingFeatures((prev) => ({ ...prev, [feature.id]: true }));
    setErrors((prev) => ({ ...prev, [feature.id]: null }));

    try {
      await feature.updateFunction(currentVenue.id, newValue);

      // Update Redux state to keep it in sync
      dispatch(
        setVenueInfo({
          ...currentVenue,
          [feature.venueKey]: newValue,
        })
      );
    } catch (err) {
      console.error(`Error updating ${feature.title}:`, err);
      setErrors((prev) => ({
        ...prev,
        [feature.id]: `Failed to update ${feature.title}. Please try again.`,
      }));
    } finally {
      setUpdatingFeatures((prev) => ({ ...prev, [feature.id]: false }));
    }
  };

  return (
    <FeatureSettingsContainer>
      <FeatureSettingsHeader>
        <Typography variant="h6">Feature Settings</Typography>
        <Typography variant="subtitle2">
          <em>Enable or disable features for this venue.</em>
        </Typography>
      </FeatureSettingsHeader>

      <FeaturesList>
        {FEATURES.map((feature) => {
          const isEnabled = currentVenue?.[feature.venueKey] ?? false;
          const isUpdating = updatingFeatures[feature.id];
          const error = errors[feature.id];

          return (
            <FeatureItem key={feature.id}>
              <FeatureInfo>
                <FeatureTitle variant="body1">
                  <strong>{feature.title}</strong>
                </FeatureTitle>
                <FeatureDescription variant="body2">{feature.description}</FeatureDescription>
                {error && (
                  <Typography variant="caption" color="error" style={{ marginTop: '4px' }}>
                    {error}
                  </Typography>
                )}
              </FeatureInfo>

              <FeatureControl>
                {isUpdating && <CircularProgress size={20} />}
                <StyledSwitch
                  checked={isEnabled}
                  onChange={(e) => handleToggle(feature, e.target.checked)}
                  disabled={isUpdating}
                  ariaLabel={`Toggle ${feature.title}`}
                />
              </FeatureControl>
            </FeatureItem>
          );
        })}
      </FeaturesList>
    </FeatureSettingsContainer>
  );
};

export default FeatureSettings;
